import { useMemo } from "react";
import { IFullAircraftConfiguration, IAircraft } from "@vmosolutions/oc-interfaces";
import { Formik, Form, FormikProps, FormikHelpers } from "formik";
import { FormikTextField } from "../../../../MUI5FormikComponents/FormikWrappers";
import { Grid } from "@mui/material";
import * as Yup from "yup";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { useDispatch } from "react-redux";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { AircraftSaveBar } from "../AircraftSaveBar";
import { v1 } from "uuid";

interface IAircraftConfigurationProps {
  selectedAircraft: IAircraft;
  selectedConfiguration?: IFullAircraftConfiguration | null;
  updateAircraftConfigurationList?: (
    aircraftConfiguration?: IFullAircraftConfiguration,
    mode?: "create" | "update" | "delete"
  ) => void;
  className?: string;
  setActiveModal?: (activeModal: IActiveModal) => void;
  configs: IFullAircraftConfiguration[] | undefined;
  setForm: any;
  setCurrentConfig: any;
  stagedAircraft: IAircraft;
  setStagedAircraft: any;
  stagedConfig: IFullAircraftConfiguration[];
  setStagedConfig: any;
}

// Aircraft Configuration validation with YUP
const AircraftConfigurationSchema = Yup.object().shape({
  aircraftId: Yup.string().required("Aircraft ID is required").nullable(),
  title: Yup.string().required("Title is required").nullable(),
  description: Yup.string().required("Description is required").nullable(),
});

type AircraftConfigurationFormikProps = FormikProps<IFullAircraftConfiguration>;

/**
 * Form to edit selected aircraft
 * @param props - selected aircraft
 */
export const AircraftConfigurationForm = (props: IAircraftConfigurationProps): JSX.Element => {
  const dispatch = useDispatch();
  const {
    selectedAircraft,
    selectedConfiguration,
    updateAircraftConfigurationList,
    setActiveModal,
    configs,
    setForm,
    setCurrentConfig,
    stagedAircraft,
    setStagedAircraft,
    stagedConfig,
    setStagedConfig,
  } = props;

  const initialAircraftConfigurationValues: IFullAircraftConfiguration = useMemo(
    (): IFullAircraftConfiguration => ({
      _id: null,
      aircraftId: stagedAircraft ? stagedAircraft?._id : selectedAircraft?._id,
      title: "",
      description: "",
      revisions: [],
      foreignRef: [],
    }),
    [selectedAircraft, stagedAircraft]
  );

  const initialValues = useMemo(() => {
    return selectedConfiguration ? selectedConfiguration : initialAircraftConfigurationValues;
  }, [initialAircraftConfigurationValues, selectedConfiguration]);

  const updateConfig = async (
    values: IFullAircraftConfiguration,
    helpers: FormikHelpers<IFullAircraftConfiguration>
  ): Promise<void> => {
    const { setSubmitting, setStatus } = helpers;
    if (stagedConfig.length > 0) {
      setStatus({ success: true });
      if (updateAircraftConfigurationList) updateAircraftConfigurationList(values, "update");
      setCurrentConfig([values]);
      setForm("revision");
      setSubmitting(false);
    } else {
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: "Updating Aircraft Configuration...",
        successMessage: "Aircraft Configuration Successfully Updated.",
        errorMessage: "Error: Unable to Update Aircraft Configuration",
      };
      const response = await AmplifyAPIWrapper.Put<IFullAircraftConfiguration>(
        `/aircraftConfiguration/${values._id}`,
        values,
        notifications
      );
      if (response?._id) {
        setStatus({ success: true });
        if (updateAircraftConfigurationList) updateAircraftConfigurationList(response, "update");
        setCurrentConfig(response);
      } else {
        setStatus({ success: false });
      }
      setSubmitting(false);
    }
  };

  const createConfig = (values: IFullAircraftConfiguration): void => {
    setCurrentConfig(values);
    setForm("revision");
    if (updateAircraftConfigurationList) updateAircraftConfigurationList(values, "create");
  };

  const handleConfigSubmit = (
    values: IFullAircraftConfiguration,
    helpers: FormikHelpers<IFullAircraftConfiguration>
  ): void => {
    if (values._id) {
      updateConfig(values, helpers);
    } else {
      createConfig(values);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleConfigSubmit}
      validationSchema={AircraftConfigurationSchema}
    >
      {(aircraftConfigurationForm: AircraftConfigurationFormikProps) => {
        const { dirty, isValid, isSubmitting, handleSubmit } = aircraftConfigurationForm;

        return (
          <Form>
            <Grid container>
              <Grid item xs={12}>
                <AircraftSaveBar
                  configs={configs}
                  aircraft={selectedAircraft}
                  currentConfig={selectedConfiguration}
                  saveAirCraftConfig={handleSubmit}
                  mode={"config"}
                  disabled={!isValid || isSubmitting}
                  hasError={!isValid}
                  stagedAircraft={stagedAircraft}
                  stagedConfig={stagedConfig}
                  dirty={dirty}
                />
              </Grid>
              <Grid
                container
                item
                direction="row"
                sm={6}
                sx={{ p: 2, overflowY: "auto", maxHeight: "calc(100svh - 290px)" }}
              >
                <Grid container className="required_fields">
                  <FormikTextField
                    className="field textfield"
                    label="Title"
                    id="configuration_title_input"
                    name="title"
                  />
                  <FormikTextField
                    className="field textfield multiline_short"
                    label="Description"
                    name="description"
                    multiline={true}
                    minRows={3}
                    maxRows={5}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
